import _capitalize from 'lodash/capitalize';
import _startCase from 'lodash/startCase';
import DescriptionField from '@/components/manage/DescriptionField';
import formatDate from 'date-fns/format';
import gql from 'graphql-tag';
import ManageFilters from '@/components/manage/Filters';
import { createStore } from '@src/lib/manage';
import { JobWhereManyInput, SegmentWhereManyInput, UserWhereManyInput } from '@src/models/where';

export const icon = 'chart-pie';
export const name = 'Segments';

export const RelationSegmentFragment = gql`
  fragment RelationSegmentDetails on Segment {
    id
    title
    usersConnection {
      total
    }
    jobsConnection {
      total
    }
    contentSharesConnection {
      total
    }
    # rewardsConnection {
    #   total
    # }
    createdAt
    status
  }
`;

export const listSchema = {
  query: gql`
    query ListSegmentEntries($where: SegmentWhereManyInput, $order: SegmentOrderByInput) {
      company {
        id
        segmentsConnection(where: $where, order: $order) {
          edges {
            entry {
              ...RelationSegmentDetails
              isDefault
            }
          }
          total
        }
      }
    }
    ${RelationSegmentFragment}
  `,
  update({ company: { segmentsConnection: { edges, total } = {} } = {} } = {}) {
    return {
      edges: (Array.isArray(edges) ? edges : []).map(({ entry }) => entry),
      total,
    };
  },
  where: { status: 'ACTIVE' },
  whereOptions: SegmentWhereManyInput,
  order: 'CREATED_ASC',
  primaryKey: 'id',
  columns() {
    const countFormatter = value => typeof value === 'number' ? value : '—';
    const columns = [
      { key: 'title', sortable: true },
      { key: 'usersConnection.total', label: 'Users', formatter: countFormatter },
      { key: 'jobsConnection.total', label: 'Jobs', formatter: countFormatter },
      { key: 'contentSharesConnection.total', label: 'Content Shares', formatter: countFormatter },
      // { key: 'rewardsConnection.total', label: 'Rewards', formatter: countFormatter },
    ];

    if (!this.modalOpts) {
      columns.push({ key: 'createdAt', formatter: value => formatDate(value, 'DD/MM/YYYY HH:MM'), sortable: true });
      columns.push({ key: 'status', formatter: value => _capitalize(`${value}`.toLowerCase()) });
    }

    return columns;
  },
  canClickRow: ({ isDefault }) => !isDefault,
};

const entryFragment = gql`
  fragment ManageSegmentDetails on Segment {
    id
    title
    description
    hidden

    usersConnection {
      total
      where
    }
    jobsConnection {
      total
      where
    }
    contentSharesConnection {
      total
      where
    }
    # rewardsConnection {
    #   total
    #   where
    # }

    createdAt
    updatedAt
    status
  }
`;

export const editSchema = {
  page: {
    title() {
      return this.isCreate ? 'New Segment' : 'Edit Segment';
    },
    breadcrumb() {
      return this.entryID === 'new' ? 'New' : 'Edit';
    },
  },
  apolloReadEntry: {
    query: gql`
      query ReadSegmentEntry($entryID: ID!) {
        company {
          id
          value: segment(where:{ id: $entryID }) {
            ...ManageSegmentDetails
          }
        }
      }
      ${entryFragment}
    `,
    result() {
      if (this.value && this.value.id) {
        [ 'users', 'jobs', 'contentShares', 'rewards' ].forEach(key => {
          const { where } = this.value[`${key}Connection`] || {};
          this.value[key] = JSON.parse(where || null);
        });
      }
    }
  },
  apolloCreateEntry: {
    validate(create) {
      this.$assert(create.title, 'Missing segment title');
    },
    mutation: gql`
      mutation CreateSegmentEntry($create: CreateSegmentInput!) {
        value: createSegment(create: $create) {
          ...ManageSegmentDetails
        }
      }
      ${entryFragment}
    `,
  },
  apolloUpdateEntry: {
    mutation: gql`
      mutation UpdateSegmentEntry($entryID: ID!, $update: UpdateSegmentInput!) {
        value: updateSegment(update: $update, where: { id: $entryID }) {
          ...ManageSegmentDetails
        }
      }
      ${entryFragment}
    `,
  },
  form: {
    $main: {
      props: {
        data: {
          type: Object,
          default: () => null,
        },
        value: {
          type: Object,
          default: () => ({}),
        },
      },
      render(el) {
        const { value } = this;
        const { isSuperAdmin } = this.$root;
        const { getValue, setValue } = createStore.call(this, value);

        const rows = [];

        rows.push(el('b-form-group', {
          props: { label: 'Title', 'label-for': 'title', 'label-cols-sm': 3 },
        }, [
          el('b-input', {
            props: { value: getValue('title') },
            domProps: { id: 'title' },
            on: { input: value => setValue('title', value) },
          }),
        ]), el('b-form-group', {
          props: { label: 'Description', 'label-for': 'description', 'label-cols-sm': 3 },
        }, [
          el(DescriptionField, {
            props: {
              extensions: [ 'strong', 'italic', 'strike', 'link' ],
              placeholder: 'Enter a description',
              value: getValue('description'),
            },
            domProps: { id: 'description' },
            on: { input: value => setValue('description', value) },
          }),
        ]));

        if (isSuperAdmin) {
          rows.push(el('b-form-group', {
            props: { label: 'Admin-only?', 'label-cols-sm': 3 },
          }, [
            el('b-checkbox', {
              class: 'py-1',
              props: { checked: getValue('hidden'), switch: true },
              domProps: { id: 'hidden' },
              on: { input: value => setValue('hidden', value) },
            }, 'Yes'),
            el('b-form-text', {}, 'Enable this if the segment should be hidden from the client'),
          ]));
        }

        const whereOptions = {
          users: UserWhereManyInput,
          jobs: JobWhereManyInput,
          contentShares: UserWhereManyInput,
          // rewards: UserWhereManyInput,
        };

        rows.push(el('b-tabs', { class: 'my-5' }, Object.keys(whereOptions).map(key => {
          const { total } = value[`${key}Connection`] || {};
          return el('b-tab', {
            props: {
              title: `${_startCase(key)} (${typeof total === 'number' ? total : '—'})`,
              lazy: true,
            },
          }, [
            el(ManageFilters, {
              props: {
                options: whereOptions[key],
                value: getValue(key) || null,
              },
              on: {
                input: value => setValue(key, value),
              },
            }),
          ]);
        })));

        return el('div', {}, rows);
      },
    },
    $sidebar: {
      props: {
        data: {
          type: Object,
          default: () => null,
        },
        value: {
          type: Object,
          default: () => ({}),
        },
      },
      render(el) {
        return el('b-table', {
          class: 'status-table',
          props: {
            stacked: true,
            fields: [
              { key: 'id', label: 'ID' },
              { key: 'createdAt', label: 'Created At', formatter: value => formatDate(value, 'DD/MM/YYYY HH:MM') },
              { key: 'updatedAt', label: 'Updated At', formatter: value => formatDate(value, 'DD/MM/YYYY HH:MM') },
              { key: 'status', label: 'Status', formatter: value => _capitalize(`${value}`.toLowerCase()) },
            ],
            items: this.value ? [ this.value ] : [],
          },
        });
      },
    },
  },
};



// WEBPACK FOOTER //
// ./src/models/Segments.js